import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { HeaderNavRect } from '../models/header-data.model';
import { NotificationObj } from '../models/notification-model';

@Injectable({
  providedIn: 'root'
})
export class HeaderWrapperService {
  private _skipContentFocus$ = new BehaviorSubject(false);
  private _focusMainContent$ = new BehaviorSubject(false);

  private _linkFocus$ = new BehaviorSubject<ElementRef | null>(null);
  private _linkActive$ = new BehaviorSubject<ElementRef | null>(null);
  private _sublinkActive$ = new BehaviorSubject<HTMLElement | null>(null);

  private _mobileSublinkActive$ = new BehaviorSubject<HTMLElement | null>(null);
  private _mobileMenuActive$ = new BehaviorSubject(false);
  private _mobileMenuOpen$ = new BehaviorSubject(false);
  private _mobileNotificationMenuOpen$ = new BehaviorSubject(false);
  private _mobileMenuReset$ = new BehaviorSubject(false);
  private _mobileMenuOpenItem$ = new BehaviorSubject<HTMLElement | null>(null);

  private _notificationToDismiss$ = new BehaviorSubject<NotificationObj>({});

  private _loaded$ = new BehaviorSubject(false);

  private _headerNavRect$ = new BehaviorSubject<HeaderNavRect>({});

  private _routerStateUrl$ = new BehaviorSubject('');

  // Used to reset the roofline mover
  private linkActive: ElementRef | null = null;

  constructor() {}

  get routerStateUrl$() {
    return this._routerStateUrl$.asObservable();
  }

  get loaded$() {
    return this._loaded$.asObservable();
  }

  get headerNavRect$() {
    return this._headerNavRect$.asObservable();
  }

  get skipContentFocus$() {
    return this._skipContentFocus$.asObservable();
  }

  get linkFocus$() {
    return this._linkFocus$.asObservable();
  }

  get linkActive$() {
    return this._linkActive$.asObservable();
  }

  get sublinkActive$() {
    return this._sublinkActive$.asObservable();
  }

  get mobileSublinkActive$() {
    return this._mobileSublinkActive$.asObservable();
  }

  get mobileMenuActive$() {
    return this._mobileMenuActive$.asObservable();
  }

  get mobileMenuOpen$() {
    return this._mobileMenuOpen$.asObservable();
  }

  get mobileNotificationMenuOpen$() {
    return this._mobileNotificationMenuOpen$.asObservable();
  }

  get mobileMenuReset$() {
    return this._mobileMenuReset$.asObservable();
  }

  get mobileMenuOpenItem$() {
    return this._mobileMenuOpenItem$.asObservable();
  }

  get notificationToDismiss$() {
    return this._notificationToDismiss$.asObservable();
  }

  get focusMainContent$() {
    return this._focusMainContent$.asObservable();
  }

  setRouterStateUrl(routerStateUrl: string) {
    this._routerStateUrl$.next(routerStateUrl);
  }

  setLoaded(loaded: boolean) {
    this._loaded$.next(loaded);
  }

  setHeaderNavRect(rect: HeaderNavRect) {
    this._headerNavRect$.next(rect);
  }

  setSkipContentFocus(skipContentFocus: boolean) {
    this._skipContentFocus$.next(skipContentFocus);
  }

  setLinkFocus(linkFocus: ElementRef | null) {
    this._linkFocus$.next(linkFocus);
  }

  setLinkActive(linkActive: ElementRef) {
    this.linkActive = linkActive;
    this._linkActive$.next(linkActive);
  }

  // This makes the sub recalc the position of the active link
  resetRooflineMover() {
    this._linkActive$.next(this.linkActive);
  }

  setSublinkActive(sublinkActive: HTMLElement) {
    this._sublinkActive$.next(sublinkActive);
  }

  setMobileSublinkActive(mobileSublinkActive: HTMLElement) {
    this._mobileSublinkActive$.next(mobileSublinkActive);
  }

  setMobileMenuActive(mobileMenuActive: boolean) {
    this._mobileMenuActive$.next(mobileMenuActive);
  }

  setMobileNotificationMenuOpen(mobileNotificationMenuOpen: boolean) {
    this._mobileNotificationMenuOpen$.next(mobileNotificationMenuOpen);
  }

  setMobileMenuOpen(mobileMenuOpen: boolean) {
    this._mobileMenuOpen$.next(mobileMenuOpen);
  }

  setMobileMenuReset(mobileMenuReset: boolean) {
    this._mobileMenuReset$.next(mobileMenuReset);
  }

  setMobileMenuOpenItem(mobileMenuOpenItem: HTMLElement) {
    this._mobileMenuOpenItem$.next(mobileMenuOpenItem);
  }

  setNotificationToDismiss(notificationToDismiss: NotificationObj) {
    this._notificationToDismiss$.next(notificationToDismiss);
  }

  setFocusMainContent(focusMainContent: boolean) {
    this._focusMainContent$.next(focusMainContent);
  }

  buildNotificationMessageArray(
    notifications: Array<{
      number?: number;
      policyNumber?: string;
      ids?: [];
      policyType?: string;
    }>,
    experienceId?: string
  ): NotificationObj {
    const itemArray: NotificationObj = {
      width: 320,
      messagesArray: []
    };

    if (notifications?.length) {
      notifications.forEach(notification => {
        itemArray.messagesArray?.push({
          linkText: this.buildNotificationLinkText(
            notification?.number,
            notification?.policyNumber
          ),
          policyNumber: notification?.policyNumber,
          ids: notification?.ids,
          linkRoute: experienceId
            ? '/' +
              experienceId +
              '/policies/' +
              notification?.policyType +
              '/' +
              notification?.policyNumber
            : '/policies/' + notification?.policyType + '/' + notification?.policyNumber,
          queryParams: { openAccordion: 'documents', tid: 'newdocnotice' },
          linkTestId: 'notification-' + notification?.policyNumber + '-Link',
          mobileLinkTestId: 'mobileNotification-' + notification?.policyNumber + '-Link'
        });
      });
    }
    return itemArray;
  }

  buildNotificationLinkText(
    notificationNumber: number | undefined,
    policyNumber: string | undefined
  ) {
    if (notificationNumber && notificationNumber > 1) {
      return (
        'You have ' + notificationNumber + ' new documents ready for Policy Number ' + policyNumber
      );
    } else {
      return 'Your new document is ready for Policy Number ' + policyNumber;
    }
  }

  centerSubNav(
    linkElem: ElementRef | undefined,
    subLinkElem: ElementRef | undefined,
    headerNavRect: HeaderNavRect | null | undefined
  ) {
    // This centers the sub menu under the nav item.
    // Needed to get accurate height, maybe switch to use route change complete
    let subNavItemOverflowsRight: boolean;
    if (linkElem && subLinkElem) {
      let subLinkElemOffset = 0;
      // Get the width of this element
      const linkElemWidth = linkElem.nativeElement.getBoundingClientRect().width;
      const subNavItemRightPosition = subLinkElem.nativeElement.getBoundingClientRect().right;

      // Get the width of the sub menu
      const subLinkElemWidth = subLinkElem.nativeElement.getBoundingClientRect().width;
      // Don't allow the sub menu to be narrower than the nav item
      subLinkElem.nativeElement.style.minWidth = linkElemWidth + 'px';

      subNavItemOverflowsRight = subNavItemRightPosition >= (headerNavRect?.right ?? 0);

      // If the sub menu is wider than the nav item
      if (subLinkElemWidth > linkElemWidth) {
        // Add the two together, half that and flip it to a negative number
        subLinkElemOffset = ((subLinkElemWidth - linkElemWidth) * -1) / 2;
      }

      if (!subNavItemOverflowsRight) {
        // Use this value to negatively offset the sub menu to the left, centering it.
        subLinkElem.nativeElement.style.left = subLinkElemOffset + 'px';
        subLinkElem.nativeElement.style.right = 'auto';
      } else {
        // Or reset things
        subLinkElem.nativeElement.style.left = 'auto';
        subLinkElem.nativeElement.style.right = '0px';
      }
    }
  }
}
